// Inventory 3D previews: the little spinning model in every OWNED inventory slot.
//
// ONE canvas and ONE WebGL context for the whole grid, not one per slot. Each slot is drawn by pointing a
// camera at that slot's model and rendering into the slot's own scissored viewport — so 30 spinning buildings
// cost 30 tiny viewport draws on one 900×300-ish canvas, instead of 30 canvases each with their own context
// (browsers cap those at ~16 and evict the oldest, which would blank half the grid).
//
// Models come from the factory's own makeMesh(), so a preview is literally the building you will place —
// no second art path to keep in sync. Unowned slots are never built or drawn: the HUD shows a "?" there.

import * as THREE from "three";

export interface PreviewSlot {
  key: string;                 // building kind
  el: HTMLElement;             // the slot's DOM cell — its rect drives the viewport
}

export class InventoryPreview {
  readonly canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private cam: THREE.PerspectiveCamera;
  private rig = new THREE.Group();          // one child per live preview, parked apart so cameras never overlap
  private built = new Map<string, THREE.Object3D>();
  private slots: PreviewSlot[] = [];
  private spin = 0;
  private host: HTMLElement;

  constructor(host: HTMLElement) {
    this.host = host;
    this.canvas = document.createElement("canvas");
    this.canvas.className = "fx-inv3d";
    // Parent OUTSIDE the grid, INSIDE the backdrop panel: the grid scrolls (a canvas within it would slide
    // against its own viewports), and the panel's background must paint UNDER the models. Inserting before the
    // grid puts the canvas between the two — backdrop, then models, then slot chrome and text.
    const panel = host.parentElement ?? host;
    panel.insertBefore(this.canvas, host);
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true });
    this.renderer.setClearColor(0x000000, 0);
    this.cam = new THREE.PerspectiveCamera(30, 1, 1, 4000);
    // Lighting that reads the models' flat colors without washing them: a key, a cool fill, and a little ambient.
    const key = new THREE.DirectionalLight(0xffffff, 2.1); key.position.set(120, 200, 140);
    const fill = new THREE.DirectionalLight(0x88bbff, 0.7); fill.position.set(-140, 60, -90);
    this.scene.add(key, fill, new THREE.AmbientLight(0xffffff, 0.55), this.rig);
  }

  /** Which slots currently hold something. Models are built once and cached; dropping to zero just stops drawing. */
  setSlots(slots: PreviewSlot[], build: (kind: string) => THREE.Object3D | null): void {
    this.slots = slots;
    for (const s of slots) {
      if (this.built.has(s.key)) continue;
      const o = build(s.key);
      if (!o) continue;
      // Re-center on the model's own bounds so every building frames the same regardless of how it was authored
      // (droppers overhang, the vat is 120 tall, belts are flat) — otherwise half the grid renders off-frame.
      const box = new THREE.Box3().setFromObject(o);
      const c = box.getCenter(new THREE.Vector3());
      o.position.sub(c);
      const holder = new THREE.Group();
      holder.add(o);
      // Frame the SPIN FOOTPRINT, not the 3D diagonal. size.length() is corner-to-corner, which for a boxy
      // building is ~1.7× its widest face — framing that threw away a third of every cell. What actually has to
      // fit is the height, and the horizontal diagonal it sweeps as it turns.
      const sz = box.getSize(new THREE.Vector3());
      holder.userData.radius = Math.max(1, Math.max(sz.y, Math.hypot(sz.x, sz.z)) * 0.5);
      holder.position.set(this.built.size * 10000, 0, 0); // parked far apart; only one is ever in frame
      this.rig.add(holder);
      this.built.set(s.key, holder);
    }
  }

  /** Spin + draw every owned slot into its own viewport. Call once a frame while the inventory is visible. */
  render(dt: number): void {
    if (!this.slots.length) { this.canvas.style.display = "none"; return; }
    this.canvas.style.display = "";
    this.spin += dt * 0.9;
    // Size + place the canvas over the grid's VISIBLE box (a scroller's client rect, not its content height).
    // Offsets are measured against the PANEL (the canvas's offsetParent), never the viewport — the panel is
    // transformed, so viewport coordinates here put the canvas somewhere else entirely and nothing appears.
    const hb = this.host.getBoundingClientRect();
    this.canvas.style.left = `${this.host.offsetLeft}px`;
    this.canvas.style.top = `${this.host.offsetTop}px`;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(hb.width)), h = Math.max(1, Math.round(hb.height));
    if (this.canvas.width !== w * dpr || this.canvas.height !== h * dpr) {
      this.renderer.setPixelRatio(dpr);
      this.renderer.setSize(w, h, false);
      this.canvas.style.width = `${w}px`;
      this.canvas.style.height = `${h}px`;
    }
    this.renderer.setScissorTest(true);
    this.renderer.clear();
    for (const s of this.slots) {
      const holder = this.built.get(s.key);
      if (!holder) continue;
      const r = s.el.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) continue;
      // CULL what is scrolled out of the frame — off-screen slots cost nothing at all.
      if (r.bottom <= hb.top || r.top >= hb.bottom) continue;
      const x = r.left - hb.left, y = hb.bottom - r.bottom;      // GL viewport origin is bottom-left
      // A slot straddling the edge draws only its visible part: the VIEWPORT keeps the model's framing intact
      // while the SCISSOR clips it, so a half-scrolled model is cropped rather than squashed.
      const visTop = Math.max(r.top, hb.top), visBot = Math.min(r.bottom, hb.bottom);
      const sy = hb.bottom - visBot, sh = Math.max(1, visBot - visTop);
      this.renderer.setViewport(x, y, r.width, r.height);
      this.renderer.setScissor(x, sy, r.width, sh);
      holder.rotation.y = this.spin;
      const rad = holder.userData.radius as number;
      // Frame the model: pull the camera back far enough that its bounding sphere fits the (short) slot axis.
      const dist = rad / Math.tan((this.cam.fov * Math.PI) / 360) * 1.02; // ~no slack: the model fills the cell
      this.cam.aspect = r.width / Math.max(1, r.height);
      this.cam.position.set(holder.position.x + dist * 0.62, dist * 0.52, dist * 0.62);
      this.cam.lookAt(holder.position.x, 0, 0);
      this.cam.updateProjectionMatrix();
      this.renderer.render(this.scene, this.cam);
    }
    this.renderer.setScissorTest(false);
  }

  dispose(): void {
    this.renderer.dispose();
    this.canvas.remove();
  }
}
